import { SplineChartV2DataPoint } from './SplineChartV2';

/**
 * Minimal field configuration needed for calculating metric totals.
 * This is a subset of FieldDefinition that works with both full field definitions
 * and the partial field objects returned by INDEX_FIELDS.getField().
 */
export interface MetricFieldConfig {
  /** The aggregation function - can be a string or expression object for computed fields */
  aggregationFunction?: string | { variables?: () => unknown };
  /** Type of aggregation function: 'computed' | 'derived' | undefined */
  aggregationFunctionType?: string;
  /** How to aggregate over time periods: 'lastValue' | 'simple' | 'trueAvg' */
  timePeriodAggregationFunctionType?: string;
  /** The time period aggregation function: 'avg' | 'sum' */
  timePeriodAggregationFunction?: string;
}

/**
 * Options for calculating metric totals
 */
export interface CalculateMetricTotalOptions {
  /**
   * Whether this is a "Last Week with Prior Year" comparison scenario.
   * When true, we show YTD data on the chart but the legend shows just the last week value.
   */
  isLastWeekWithPriorYear?: boolean;

  /**
   * Optional aggregation function override
   */
  aggregationFunctionOverride?: string;
}

/**
 * Options for calculating comparison metric totals
 */
export interface CalculateComparisonTotalOptions extends CalculateMetricTotalOptions {
  /**
   * The comparison week IDs to include in the total (from the API).
   * Only data points with weekIds in this array will be summed.
   * If not provided, all comparison data points are included.
   */
  comparisonWeekIdsToInclude?: number[];
}

/**
 * Determines the aggregation function to use for calculating totals.
 *
 * For computed/derived fields, the `aggregationFunction` is an expression object,
 * so we need to check `timePeriodAggregationFunction` instead.
 *
 * @param field - The field configuration
 * @param aggregationFunctionOverride - Optional override for the aggregation function
 * @returns The aggregation function string to use ('sum', 'avg', 'stats', 'cardinality', etc.)
 */
export const getAggregationFunctionForTotal = (
  field: MetricFieldConfig,
  aggregationFunctionOverride?: string
): string => {
  // If an override is provided, use it
  if (aggregationFunctionOverride) {
    return aggregationFunctionOverride;
  }

  // For computed/derived fields, the aggregationFunction is an expression object
  // We should use timePeriodAggregationFunction to determine how to aggregate
  if (field.aggregationFunctionType === 'computed' || field.aggregationFunctionType === 'derived') {
    return field.timePeriodAggregationFunction || 'avg';
  }

  // For regular fields, aggregationFunction is a string
  if (typeof field.aggregationFunction === 'string') {
    return field.aggregationFunction;
  }

  // Default to sum
  return 'sum';
};

/**
 * Calculates the total value for a metric from SplineChartV2 data points.
 *
 * This follows the same logic as V1's getMetricTotalValue function:
 * 1. For 'lastValue' type fields or cardinality, returns the last value
 * 2. For 'avg' or 'stats' aggregation, returns the average
 * 3. For computed fields, uses timePeriodAggregationFunction to determine aggregation
 * 4. Otherwise, returns the sum
 *
 * @param dataPoints - The data points to calculate the total from
 * @param field - The field configuration for this metric
 * @param options - Additional options for the calculation
 * @returns The calculated total value
 */
export const calculateMetricTotal = (
  dataPoints: SplineChartV2DataPoint[],
  field: MetricFieldConfig,
  options: CalculateMetricTotalOptions = {}
): number => {
  const { isLastWeekWithPriorYear = false, aggregationFunctionOverride } = options;

  // Filter out null values
  const validDataPoints = dataPoints.filter((d) => d.value !== null);

  if (validDataPoints.length === 0) {
    return 0;
  }

  const values = validDataPoints.map((d) => d.value as number);
  const aggregationFunction = getAggregationFunctionForTotal(field, aggregationFunctionOverride);

  // When "Last Week with Prior Year", always return the last value regardless of
  // aggregationFunctionOverride — the data range was expanded for chart display only,
  // so the legend must show just the target week's value.
  // If the override explicitly requests "lastValue", always return the latest data point.
  // For cardinality / lastValue fields, field defaults apply when there is no override.
  if (
    isLastWeekWithPriorYear ||
    aggregationFunction === 'lastValue' ||
    (!aggregationFunctionOverride &&
      (aggregationFunction === 'cardinality' || field.timePeriodAggregationFunctionType === 'lastValue'))
  ) {
    return validDataPoints[validDataPoints.length - 1].value as number;
  }

  // Calculate the sum first (used by both sum and avg)
  const total = values.reduce((acc, val) => acc + val, 0);

  // For avg or stats aggregation, return the average
  if (aggregationFunction === 'avg' || aggregationFunction === 'stats') {
    return total / values.length;
  }

  // Default to sum
  return total;
};

/**
 * Calculates the comparison total using the comparison week IDs from the API.
 *
 * For Prior Year comparisons, we use the API-provided comparison week IDs
 * to filter the comparison data. This properly handles 52/53 week year mappings.
 *
 * @param comparisonDataPoints - The comparison data points
 * @param field - The field configuration for this metric
 * @param options - Additional options including the comparison week IDs to include
 * @returns The calculated comparison total value
 */
export const calculateComparisonTotal = (
  comparisonDataPoints: SplineChartV2DataPoint[],
  field: MetricFieldConfig,
  options: CalculateComparisonTotalOptions = {}
): number => {
  const { comparisonWeekIdsToInclude, ...restOptions } = options;

  // If specific comparison week IDs are provided, filter to only those weeks
  // These come from the API and properly map main period weeks to comparison weeks
  const filteredDataPoints =
    comparisonWeekIdsToInclude && comparisonWeekIdsToInclude.length > 0
      ? comparisonDataPoints.filter((d) => comparisonWeekIdsToInclude.includes(d.weekId))
      : comparisonDataPoints;

  return calculateMetricTotal(filteredDataPoints, field, restOptions);
};

/**
 * Finds the comparison value for a specific comparison week ID.
 *
 * Used for "Last Week with Prior Year" scenarios where we need to find
 * the comparison value for a specific week (provided by the API).
 *
 * @param comparisonDataPoints - The comparison data points
 * @param comparisonWeekId - The comparison week ID to find (from the API)
 * @returns The comparison value for the matching week, or null if not found
 */
export const findComparisonValueForWeekId = (
  comparisonDataPoints: SplineChartV2DataPoint[],
  comparisonWeekId: number
): number | null => {
  const matchingPoint = comparisonDataPoints.find((d) => d.weekId === comparisonWeekId);
  return matchingPoint?.value ?? null;
};
